import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { DecisionStep, Flow, FlowStep, StepTransition } from "../../../../types";
import { TransitionEditor } from "./TransitionEditor";

const schema = z.object({
  field_key: z.string().min(1, "Campo obbligatorio"),
  operator: z.enum([">", "<", ">=", "<=", "==", "!=", "in", "not_in"]),
  value: z.string().min(1, "Valore obbligatorio"),
  order: z.number().int().min(0),
});

type FormData = z.infer<typeof schema>;

interface Props {
  step: DecisionStep;
  steps: FlowStep[];
  flows: Flow[];
  onSave: (updated: DecisionStep) => void;
}

// Available fields: collect field_keys + tool_call output_fields
function getAvailableFields(steps: FlowStep[]): string[] {
  const fields: string[] = [];
  for (const s of steps) {
    if (s.type === "collect") fields.push(s.field_key);
    if (s.type === "tool_call") fields.push(s.output_field);
  }
  return fields.filter((v, i, a) => a.indexOf(v) === i);
}

// Serialize decision value to string for form
function valueToString(v: DecisionStep["value"]): string {
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

// Parse string back to decision value
function parseValue(raw: string, operator: string): DecisionStep["value"] {
  if (operator === "in" || operator === "not_in") {
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
  const num = Number(raw);
  if (!isNaN(num) && raw.trim() !== "") return num;
  if (raw === "true") return true;
  if (raw === "false") return false;
  return raw;
}

const DEFAULT_TRANSITION: StepTransition = { end_flow: true, message: "Operazione conclusa." };

export const DecisionStepForm = ({ step, steps, flows, onSave }: Props) => {
  const [onTrue, setOnTrue] = useState<StepTransition>(step.on_true);
  const [onFalse, setOnFalse] = useState<StepTransition>(step.on_false);

  const collectedFields = getAvailableFields(steps);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      field_key: step.field_key,
      operator: step.operator,
      value: valueToString(step.value),
      order: step.order,
    },
  });

  useEffect(() => {
    reset({
      field_key: step.field_key,
      operator: step.operator,
      value: valueToString(step.value),
      order: step.order,
    });
    setOnTrue(step.on_true);
    setOnFalse(step.on_false);
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = handleSubmit((data) => {
    onSave({
      ...step,
      field_key: data.field_key,
      operator: data.operator,
      value: parseValue(data.value, data.operator),
      on_true: onTrue,
      on_false: onFalse,
      order: data.order,
    });
  });

  return (
    <form onSubmit={onSubmit} className="flex-col gap-2">
      <div className="flex-row gap-2">
        <Controller
          name="field_key"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth size="small" error={!!errors.field_key}>
              <InputLabel>Campo da valutare</InputLabel>
              <Select {...field} label="Campo da valutare" value={field.value ?? ""}>
                {collectedFields.length === 0 && (
                  <MenuItem value={field.value ?? ""}>{field.value || "—"}</MenuItem>
                )}
                {collectedFields.map((k) => (
                  <MenuItem key={k} value={k}>
                    {k}
                  </MenuItem>
                ))}
              </Select>
              {errors.field_key && (
                <FormHelperText>{errors.field_key.message}</FormHelperText>
              )}
            </FormControl>
          )}
        />
        <Controller
          name="operator"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth size="small" error={!!errors.operator}>
              <InputLabel>Operatore</InputLabel>
              <Select {...field} label="Operatore" value={field.value ?? ""}>
                {([">", "<", ">=", "<=", "==", "!=", "in", "not_in"] as const).map((op) => (
                  <MenuItem key={op} value={op}>
                    {op}
                  </MenuItem>
                ))}
              </Select>
              {errors.operator && (
                <FormHelperText>{errors.operator.message}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      </div>

      <Controller
        name="value"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label='Valore (per "in"/"not_in": valori separati da virgola)'
            error={!!errors.value}
            helperText={errors.value?.message}
          />
        )}
      />

      <TransitionEditor
        label="Se VERO (on_true)"
        value={onTrue}
        onChange={setOnTrue}
        steps={steps.filter((s) => s.id !== step.id)}
        flows={flows}
      />
      <TransitionEditor
        label="Se FALSO (on_false)"
        value={onFalse}
        onChange={setOnFalse}
        steps={steps.filter((s) => s.id !== step.id)}
        flows={flows}
      />

      <Box>
        <Button variant="contained" type="submit" size="small">
          Salva step
        </Button>
      </Box>
    </form>
  );
};
